/**
 * Geometry and membership maths for shape groups.
 *
 * Every value is a percentage of the slide, matching how slide objects have always
 * stored position. Nothing here touches React, Redux or the DOM, which is why the
 * subtle part of grouping is also the easily testable part.
 */

/** Smallest a member may become, in slide percentage points. */
export const MIN_MEMBER_PERCENT = 1;

/** Read `'12.5%'` as 12.5. Anything unparseable reads as 0. */
export const toPercent = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/** The geometry of a stored style object, as a box. */
export const memberBox = (styles = {}) => ({
  left: toPercent(styles.left),
  top: toPercent(styles.top),
  width: toPercent(styles.width),
  height: toPercent(styles.height),
});

/** A box back into the style strings slide objects persist. */
export const boxToStyles = (box) => ({
  left: `${box.left}%`,
  top: `${box.top}%`,
  width: `${box.width}%`,
  height: `${box.height}%`,
});

/** Union of every box. Null when there is nothing to bound. */
export const computeGroupBounds = (boxes = []) => {
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((b) => b.left));
  const top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  return { left, top, width: right - left, height: bottom - top };
};

/** Shift a box by a delta in percentage points. */
export const translateBox = (box, dx, dy) => ({
  ...box,
  left: box.left + dx,
  top: box.top + dy,
});

/**
 * Map a box out of `from` bounds and into `to` bounds. This is the arithmetic
 * PowerPoint expresses as chOff/chExt -> off/ext, applied to the member immediately
 * rather than stored on a container.
 *
 * A zero-extent source axis cannot yield a ratio, so it passes through at scale 1
 * rather than producing Infinity.
 */
export const scaleBoxInto = (box, from, to) => {
  const sx = from.width === 0 ? 1 : to.width / from.width;
  const sy = from.height === 0 ? 1 : to.height / from.height;
  return {
    left: to.left + (box.left - from.left) * sx,
    top: to.top + (box.top - from.top) * sy,
    width: box.width * sx,
    height: box.height * sy,
  };
};

/** True when `box` lies entirely inside `rect` — PowerPoint's marquee rule. */
export const isFullyEnclosed = (box, rect) =>
  box.left >= rect.left &&
  box.top >= rect.top &&
  box.left + box.width <= rect.left + rect.width &&
  box.top + box.height <= rect.top + rect.height;

/** The id of the group `memberId` belongs to, or null. */
export const groupForMember = (groups = {}, memberId) =>
  Object.keys(groups).find((gid) => groups[gid].memberIds.includes(memberId)) || null;

/**
 * Drop `memberId` from every group, dissolving any group left with fewer than two
 * members. Returns a new object; the input is not mutated.
 */
export const pruneMember = (groups = {}, memberId) => {
  const next = {};
  Object.keys(groups).forEach((gid) => {
    const memberIds = groups[gid].memberIds.filter((id) => id !== memberId);
    if (memberIds.length >= 2) next[gid] = { ...groups[gid], memberIds };
  });
  return next;
};

/** True when scaling every box into `to` keeps them all at or above the minimum. */
export const isResizeAllowed = (boxes, from, to) =>
  boxes.every((b) => {
    const scaled = scaleBoxInto(b, from, to);
    return scaled.width >= MIN_MEMBER_PERCENT && scaled.height >= MIN_MEMBER_PERCENT;
  });
